'use client';

import {createContext, useContext, Dispatch, SetStateAction, useState, useEffect, ReactNode} from "react";
import Cookies from 'js-cookie';
import DataProviderEvo from "../components/dataProviderEvo/evo-data-provider";
import SignedCheckDataProvider from "../components/dataProviderEvo/signed-check-data-provider";
import UserDetailDataProvider from "../components/dataProviderEvo/user-detail-data-provider";
import PaymentOptionsDataProvider from "../components/dataProviderEvo/payment-options-data-provider";
import LoginDataProvider from "../components/dataProviderEvo/login-data-provider";
import {ROUTE_LOGIN, ROUTE_MAIN, ROUTE_PURCHASE} from "../App";
import {useToast} from "@chakra-ui/react";
import {useNavigate} from "react-router-dom";
import LogoutDataProvider from "../components/dataProviderEvo/logout-data-provider";

type DataType = {
    name: string,
    label: string
}


interface Address {
    street: string;
    city: string;
    zipCode: string;
}

interface User {
    id: number;
    name: string;
    email: string;
    address: Address;
}

type UserData = {
    user: User; // Specify the type of the prop
};

interface ContextProps {
    userId: number | null,
    setUserId: (id: number | null) => void;
    authToken: string | null,
    setAuthToken: (id: string) => void;
    pwdHash: string | null,
    setPwdHash: (id: string) => void;
    email: string | null,
    setEmail: (id: string) => void;
    isLoggedIn: boolean,
    setIsLoggedIn: Dispatch<SetStateAction<boolean>>,
    userData: {},
    setUserData: Dispatch<SetStateAction<{}>>,
    navHistory: [],
    setNavHistory: Dispatch<SetStateAction<[]>>
    hasSubscriptionActive: boolean,
    setSubscriptionActive: Dispatch<SetStateAction<boolean>>,
    setLogout:  () => void,
    loading: boolean
}

const EvoContext = createContext<ContextProps>({
    userId: null,
    setUserId: (): void => {},
    authToken: null,
    setAuthToken: (): void => {},
    pwdHash: null,
    setPwdHash: (): void => {},
    email: null,
    setEmail: (): void => {},
    isLoggedIn: false,
    setIsLoggedIn: (): boolean => false,
    userData: {},
    setUserData:  () => {},
     navHistory: [],
     setNavHistory: () => {},
    hasSubscriptionActive: false,
    setSubscriptionActive: (): boolean => false,
    setLogout: () => {},
    loading: false
    },
)

export const EvoContextProvider = ({ children }: { children: ReactNode }) => {

    const [userId, setUserId] = useState<number | null>(null);
    const [authToken, setAuthToken] = useState<string>('');
    const [pwdHash, setPwdHash] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [hasSubscriptionActive, setSubscriptionActive] = useState<boolean>(false);
    const [userData, setUserData] = useState({});
    const [loading, setLoading] = useState(true);

    const toast = useToast();

    // const [navHistory, setNavHistory] = useState<[]>([]);
    // const [previousPage, setPreviousPage] = useState<string | null>(null);


    const [navHistory, setNavHistory] = useState<[]>(() => {
        const storedValue = localStorage.getItem('prevPage');
        return storedValue && storedValue !== 'null' ? JSON.parse(storedValue) : [];
    });

    const setLogout =  async (): Promise<boolean> => {

        try {
            setLoading(true);

            let headers = {
                "Auth-token": authToken,
                "id_user": userId
            };

            const dp = new LogoutDataProvider();
            const res = await dp.logout({}, headers);

            if (res.status === 'OK') {

                Cookies.remove('session-hash');
                Cookies.remove('PHPSESSID');
                setIsLoggedIn(false);
                setUserId(null);
                setUserData({});
                setAuthToken('');
                setEmail('');
                setPwdHash('');
                setLoading(false);

                setEmail('');
                setPwdHash('');

                return true;
            } else {
                setLoading(false);
                return false;
            }
        } catch (error) {
            setLoading(false);
            return false;
        }

    };

    const setLogin = async (): Promise<boolean> => {
        if (isLoggedIn) {
            return true;
        }

        let sessionHash = authToken != '' ?  authToken : Cookies.get('session-hash');

        if ((!sessionHash || sessionHash == undefined || sessionHash == 'undefined') &&  email.length == 0 && pwdHash.length == 0) {
            setIsLoggedIn(false);
            setLoading(false);
            return false;
        }

        try {
            setLoading(true);

            let headers = {};

            if(sessionHash !== undefined && sessionHash !== 'undefined' && sessionHash){
                headers = {
                    "Auth-token": sessionHash,
                };
            } else {
                headers = {
                    "Auth-login-name": email,
                    "Auth-login-password-b64": pwdHash
                }
            }

            // console.log(headers);

           const dp = new LoginDataProvider();
            const res = await dp.login({}, headers);

            if (res.status === 'OK') {

                let authToken = res.data.token;

                Cookies.set('session-hash', authToken, {expires: 7, path: '/', secure: true});

                setUserId(res.data.user_id);
                setAuthToken(authToken);
                setIsLoggedIn(true);
                setLoading(false);

                setEmail('');
                setPwdHash('');

                return true;
            } else {

                if(sessionHash !== undefined && sessionHash !== 'undefined' && sessionHash){
                    Cookies.remove('session-hash');
                    sessionHash = '';
                }

                setIsLoggedIn(false);
                setLoading(false);
                return false;
            }
        } catch (error) {
            setIsLoggedIn(false);
            setLoading(false);
            return false;
        }
    };

    useEffect(() => {
        const checkLogin = async () => {
            const success = await setLogin();
        };

        checkLogin();

    }, []);

    useEffect(() => {
        if (email != '' && pwdHash != '') {
            const checkLogin = async () => {
                const success = await setLogin();
                if(success){

                    toast({
                        title: 'Přihlášení proběhlo úspěšně',
                        // description: 'Přihlášení proběhlo úspěšně',
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                        position: "top"
                    });
                } else {
                    toast({
                        title: 'Přihlášení se nezdařilo',
                        description: 'Zkuste akci zopakovat.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                        position: "top"
                    });
                }

            }
            checkLogin();
        }

    }, [email, pwdHash]);


    useEffect(() => {
        if (authToken != '') {
            const checkLogin = async () => {
                const success = await setLogin();
            }
            checkLogin();
        }

    }, [authToken]);


    const getUserData = () => {

        if(userId && authToken && Object.keys(userData).length  == 0) {
            // setIsLoggedIn(true);
            // Cookies.set('session-hash', authToken, {expires: 7, path: '/', secure: true});

            let dp = new UserDetailDataProvider();
            dp.getData(userId, authToken, {}, {}).then((res) => {
                if (res && res.status === 'OK') {
                    setSubscriptionActive((res.data.has_active_subscription || res.data.isAdmin));
                    setUserData(res.data);
                    if(!userId){

                        setUserId(res.data.id_user);
                    }

                } else {
                    //     toast({
                    //         title: 'Kódem pro aktivaci předplatného',
                    //         description: res.errors[0] || 'Zkuste akci zopakovat.',
                    //         status: 'error',
                    //         duration: 5000,
                    //         isClosable: true,
                    //     });
                }
            });
        }
    };

    useEffect(() => {
        localStorage.setItem('prevPage', JSON.stringify(navHistory));
    }, [navHistory]);


    useEffect(() => {
        getUserData();
    }, [userId, authToken]);


    return (
        <EvoContext.Provider value={{userId, setUserId, authToken, setAuthToken, email, setEmail, pwdHash, setPwdHash, isLoggedIn, setIsLoggedIn, userData, setUserData, navHistory, setNavHistory, hasSubscriptionActive, setSubscriptionActive, setLogout, loading}}>
            {children}
        </EvoContext.Provider>
    )
};

export const useEvoContext = () => useContext(EvoContext);